import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Route-level code splitting: load page JS only when the route is visited
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Quote = lazy(() => import('./pages/Quote'));
const Booking = lazy(() => import('./pages/Booking'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const MoverApp = lazy(() => import('./pages/MoverApp'));
const JobPage = lazy(() => import('./pages/JobPage'));
const Track = lazy(() => import('./pages/Track'));

// Routes where the public nav/footer should not appear
const SHELL_HIDDEN = ['/admin', '/admin/reviews', '/mover', '/login', '/forgot-password', '/reset-password'];

function PageLoader() {
  return (
    <div className="app-page-loader" aria-live="polite" aria-busy="true">
      <div className="app-page-loader-spinner" />
      <span className="app-page-loader-text">Loading…</span>
    </div>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const hideShell = SHELL_HIDDEN.some(p => pathname === p || pathname.startsWith(p + '/'));

  return (
    <div className="App">
      {!hideShell && <Navbar />}

      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/"              element={<Home />} />
        <Route path="/services"      element={<Services />} />
        <Route path="/quote"         element={<Quote />} />
        <Route path="/booking"       element={<Booking />} />
        <Route path="/track"         element={<Track />} />
        <Route path="/about"         element={<About />} />
        <Route path="/contact"       element={<Contact />} />
        <Route path="/privacy"       element={<Privacy />} />
        <Route path="/terms"         element={<Terms />} />
        <Route path="/portal"        element={<CustomerPortal />} />
        <Route path="/login"         element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reviews"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mover"
          element={
            <ProtectedRoute requiredRole="mover">
              <MoverApp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mover/jobs/:id"
          element={
            <ProtectedRoute requiredRole="mover">
              <JobPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      </Suspense>

      {!hideShell && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <Router>
            <AppShell />
          </Router>
        </SiteSettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
